import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Headline } from '@xingternal/typography';

import { useTeampermissionsUserResources } from '@/hooks/useTeampermissionsUserResources';
import { useTrackPage } from '@/hooks/useTrackPage';
import { useManagedCompanies } from '@/hooks/useManagedCompanies';

import { BaseButton } from '@/components/BaseButton';
import { CompanyCard } from '@/components/CompanyCard';
import { CompanyCardContainer } from '@/components/CompanyCardContainer';
import { ShowMoreButton } from '@/components/ShowMoreButton';
import { CompanyCardsSkeletonLoggedin } from '@/components/Skeleton';
import { MetaData } from '@/components/MetaData';
import { PageError } from '@/components/PageError';
import { EmptyState } from '.';
import { EXTERNAL_LINKS } from '@/routes';
import { MANAGE_USERS_PERMISSION } from '@/config/constants';
import { pagesTrackingName, trackingParams } from '@/utils/tracking';

import styles from './ManagedCompanies.css';
import { Company } from '@/types/graphql-types';
import useAppcues from '@/hooks/useAppcues';
import { useSearchParams } from '@/hooks/useSearchParams';

const ManagedCompanies: React.FC = () => {
  const intl = useIntl();
  const { source: searchParamsSource } = useSearchParams();

  const { data, networkStatus, loading, pagination, error } =
    useManagedCompanies();
  const { data: teampermissionsData, loading: teampermissionsLoading } =
    useTeampermissionsUserResources({ permissions: MANAGE_USERS_PERMISSION });

  const currentCompanies = data?.viewer?.managedCompanies?.edges;

  const loadingFirstRender =
    teampermissionsLoading && loading && networkStatus !== 3;

  useTrackPage({ pageName: pagesTrackingName.managed });

  const dontHaveAnyCompany = currentCompanies && currentCompanies.length === 0;
  const comesFromOnlyfy = searchParamsSource === 'onlyfy-one';
  const shouldExecute = !error && dontHaveAnyCompany && comesFromOnlyfy;

  useAppcues(!!shouldExecute);

  if (loadingFirstRender) return <CompanyCardsSkeletonLoggedin />;
  if (error) return <PageError />;

  if (currentCompanies?.length === 0) return <EmptyState />;

  const { hasNextPage, endCursor } =
    data?.viewer?.managedCompanies?.pageInfo ?? {};

  const isPaginationDisabled = () => {
    if (loadingFirstRender) return true;

    return false;
  };

  const managedCompanies =
    teampermissionsData?.viewer?.teamPermissions?.userResources?.collection?.map(
      (item) => item?.resourceId,
    ) ?? [];

  return (
    <>
      <MetaData
        index={false}
        title={intl.formatMessage({ id: 'CP_MANAGED_COMPANIES_HEADER' })}
        description={intl.formatMessage({ id: 'CP_MANAGED_COMPANIES_HEADER' })}
      />
      <Headline size="medium" as="h1" className={styles.headline}>
        <FormattedMessage id="CP_MANAGED_COMPANIES_HEADER" />
      </Headline>
      <CompanyCardContainer>
        {currentCompanies?.map((item) => {
          const company = item?.node?.company as Company;
          const entityPageId =
            company?.entityPageId === null ? undefined : company?.entityPageId;
          return (
            <CompanyCard
              key={company?.id}
              company={company}
              buttons={[
                managedCompanies.includes(entityPageId) ? (
                  <BaseButton
                    className={styles.teampermissionsButton}
                    key={`teampermissions-${company?.id}`}
                    size="small"
                    href={EXTERNAL_LINKS.teampermissions({
                      companyId: company?.id,
                    })}
                    trackingAttributes={
                      trackingParams.manageTeamButtonCompanyCard
                    }
                  >
                    <FormattedMessage id="CP_TEAMPERMISSIONS" />
                  </BaseButton>
                ) : null,
                <BaseButton
                  className={styles.brandmanagerButton}
                  key={`brandmanager-${company?.id}`}
                  size="small"
                  variant="secondary"
                  href={EXTERNAL_LINKS.brandmanager({
                    slug: company?.entityPage?.slug,
                  })}
                  trackingAttributes={
                    trackingParams.brandmanagerButtonCompanyCard
                  }
                >
                  <FormattedMessage id="CP_BRANDMANAGER" />
                </BaseButton>,
              ]}
            />
          );
        })}
      </CompanyCardContainer>
      {hasNextPage && (
        <ShowMoreButton
          disabled={isPaginationDisabled()}
          loading={loading}
          onClick={() => pagination(endCursor)}
          intlKey="CP_OTHER_COMPANIES_MANAGE"
        />
      )}
    </>
  );
};

export { ManagedCompanies };
